import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, CheckCircle, XCircle, AlertTriangle, UploadCloud, Settings as SettingsIcon, BookOpen, Map, ArrowLeft, Loader2 } from 'lucide-react';
// Mock Data
const importedLearners = [{
  id: 1,
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 2,
  name: 'Michael Chen',
  email: 'mchen@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 3,
  name: 'Emily Davis',
  email: 'emily.d@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 4,
  name: 'John Smith',
  email: 'john.smith@example.com',
  status: 'warning',
  message: 'Account already exists, will update'
}, {
  id: 5,
  name: 'Lisa Wong',
  email: 'lisa.w@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 6,
  name: 'Alex Rivera',
  email: 'alex.r@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 7,
  name: 'David Kim',
  email: 'david.k@example.com',
  status: 'error',
  message: 'Invalid email format'
}, {
  id: 8,
  name: 'Rachel Green',
  email: 'rachel.g@example.com',
  status: 'success',
  message: 'Ready to import'
}];
const availableSkills = ['AI Functionality Beyond the Hype', 'Active and Empathetic Listening', 'DEI: Employee Relations', 'Developing EQ and Empathy', 'EQ for the Workplace', 'Effective Communication'];
const availablePathways = ['Leadership Essentials', 'Technical Foundations', 'Communication Mastery'];
export function BulkUploadResultsPage() {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Assignment State
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedPathways, setSelectedPathways] = useState<string[]>([]);
  const [enableAllSkills, setEnableAllSkills] = useState(false);
  const [enableAllPathways, setEnableAllPathways] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  // Sync "Enable All" toggles with individual selections
  useEffect(() => {
    if (enableAllSkills) {
      setSelectedSkills(availableSkills);
    } else if (selectedSkills.length === availableSkills.length) {
      setSelectedSkills([]);
    }
  }, [enableAllSkills]);
  useEffect(() => {
    if (selectedSkills.length === availableSkills.length && availableSkills.length > 0) {
      setEnableAllSkills(true);
    } else if (selectedSkills.length < availableSkills.length) {
      setEnableAllSkills(false);
    }
  }, [selectedSkills]);
  useEffect(() => {
    if (enableAllPathways) {
      setSelectedPathways(availablePathways);
    } else if (selectedPathways.length === availablePathways.length) {
      setSelectedPathways([]);
    }
  }, [enableAllPathways]);
  useEffect(() => {
    if (selectedPathways.length === availablePathways.length && availablePathways.length > 0) {
      setEnableAllPathways(true);
    } else if (selectedPathways.length < availablePathways.length) {
      setEnableAllPathways(false);
    }
  }, [selectedPathways]);
  const toggleSkill = (skill: string) => {
    setSelectedSkills(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  const togglePathway = (pathway: string) => {
    setSelectedPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const handleConfirm = () => {
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/assessment'); // Navigate back to users page
    }, 1500);
  };
  const stats = [{
    label: 'Total Processed',
    value: '124',
    icon: Users,
    color: 'text-[#30718D]',
    bg: 'bg-[#30718D]/10'
  }, {
    label: 'Ready to Import',
    value: '120',
    icon: CheckCircle,
    color: 'text-[#007558]',
    bg: 'bg-[#007558]/10'
  }, {
    label: 'Needs Attention',
    value: '4',
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bg: 'bg-yellow-100'
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen pb-12" data-id="element-2068" data-mp="1kq7xn77vw11i">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-2069" data-mp="1yppri101e4hq">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2070" data-mp="1ynwmsr8zhucd">
          <div className="flex items-center h-16" data-id="element-2071" data-mp="1tszclgzyxexw">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2072" data-mp="2ddzhnhdhfggp">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2073" data-mp="2ibixglptw1ar">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2074" data-mp="24d1gr7ycbp2c">
                <Home size={18} data-id="element-2075" data-mp="28fy2v8n3a3b1" />
                <span className="hidden sm:inline" data-id="element-2076" data-mp="1umq8xabjcvq6">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2077" data-mp="1zd6oazyx2rf6">
                <BarChart3 size={18} data-id="element-2078" data-mp="1kplkf3u7wo67" />
                <span className="hidden sm:inline" data-id="element-2079" data-mp="1kmfujmdrli9f">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2080" data-mp="1plao9jd9sr8b">
                <FileText size={18} data-id="element-2081" data-mp="1gn71kjqjtoex" />
                <span className="hidden sm:inline" data-id="element-2082" data-mp="1gm5roil9r7ys">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2083" data-mp="3icmpv6pbhdp3">
                <UserCog size={18} data-id="element-2084" data-mp="38ljlnw13cill" />
                <span className="hidden sm:inline" data-id="element-2085" data-mp="38mkya4xr9d5f">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2086" data-mp="552ty7h6yo9r">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2087" data-mp="538hzinwe6ht">
                <Bell size={20} data-id="element-2088" data-mp="4f6fvs68pka" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2089" data-mp="3s999xt9tk0q2"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2090" data-mp="3na62612gfgee">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2091" data-mp="2gulyf7hhtq7i">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2092" data-mp="2bxvhyj1s0slk">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2093" data-mp="wdcorexd8elf">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2094" data-mp="154wkrtamcacp" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2095" data-mp="1a4rbnwue8zxr">
                    <div className="py-2" data-id="element-2096" data-mp="1f21c07jbmw0d">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2097" data-mp="1k2fgxgpmlpu6">
                        <Users size={18} className="text-gray-600" data-id="element-2098" data-mp="1o2pq128pswd9" />
                        <span className="font-medium text-gray-900" data-id="element-2099" data-mp="3luacmmvhljqr">
                          Profile
                        </span>
                      </Link>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2100" data-mp="3px72myncwi4q">
                        <SettingsIcon size={18} className="text-gray-600" data-id="element-2101" data-mp="31yzef07vtn6s" />
                        <span className="font-medium text-gray-900" data-id="element-2102" data-mp="3fryqstcdb1a4">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2103" data-mp="3brycr9t6ks3l"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm text-[#DB3725]" data-id="element-2104" data-mp="2o0sygz2w27ve">
                        <span className="font-medium" data-id="element-2105" data-mp="2x0pjmqq2kolp">Logout</span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2106" data-mp="c95hwtujb0ma">
        {/* Page Header */}
        <div data-phoebe-tour="bulk-upload" className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-2107" data-mp="7bdsc6hd3ql9">
          <div data-id="element-2108" data-mp="ju9ek81sb3il">
            <Link to="/assessment" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-2" data-id="element-2109" data-mp="jur6isheyvkp">
              <ArrowLeft size={16} data-id="element-2110" data-mp="5wkrquo5pmaw" />
              Back to Users
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3" data-id="element-2111" data-mp="au31uodwu7kh">
              <UploadCloud className="text-[#30718D]" size={32} data-id="element-2112" data-mp="yjnlzalsvavc" />
              Upload Results
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-2113" data-mp="13hfbxz2vwmww">
              Review your imported learners and assign their initial learning
              paths.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start md:self-auto" data-id="element-2114" data-mp="tr48ser0yhr2">
            <button onClick={() => navigate('/assessment')} className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium text-sm" data-id="element-2115" data-mp="1hhq5j9vkwflr">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={isSubmitting} className="flex items-center gap-2 px-6 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium text-sm shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-2116" data-mp="2j7fxwckp4yad">
              {isSubmitting ? <>
                  <Loader2 size={18} className="animate-spin" data-id="element-2117" data-mp="2f3rr3w3w26q6" />
                  Processing...
                </> : <>
                  <CheckCircle size={18} data-id="element-2118" data-mp="20elr45x88ch8" />
                  Confirm & Import
                </>}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8" data-id="element-2119" data-mp="1rhbfgjghylbs">
          {stats.map((stat, index) => {
          const Icon = stat.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-2120" data-mp="1hh12mzydjfx5" data-mpi={index}>
                <div className={`w-12 h-12 rounded-full ${stat.bg} flex items-center justify-center flex-shrink-0`} data-id="element-2121" data-mp="xwu3xzfwq1vy">
                  <Icon size={24} className={stat.color} data-id="element-2122" data-mp="1cumgqju2kc6g" />
                </div>
                <div data-id="element-2123" data-mp="17sx2ywfgpkeo">
                  <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-2124" data-mp="17sndrb28rha1">
                    {stat.label}
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2125" data-mp="1gumx4a0qaxg5">
                    {stat.value}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2126" data-mp="1qk6h5j06gxcl">
          {/* Left Column: Imported Learners Table */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[600px]" data-id="element-2127" data-mp="24l0soaiivefh">
            <div className="p-5 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between" data-id="element-2128" data-mp="2clf1yemmtt0m">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2129" data-mp="2hip3gsnapv4t">
                Import Preview
              </h2>
              <span className="text-sm text-gray-500" data-id="element-2130" data-mp="2mct6x1xcrl15">
                Showing {importedLearners.length} of 124
              </span>
            </div>

            <div className="flex-1 overflow-auto" data-id="element-2131" data-mp="2vadcuo65u5i3">
              <table className="w-full text-left border-collapse" data-id="element-2132" data-mp="2vd7wwel9hoaz">
                <thead className="bg-white sticky top-0 z-10 shadow-sm" data-id="element-2133" data-mp="30b12p6inkgaq">
                  <tr data-id="element-2134" data-mp="359ui54hi2hdj">
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-2135" data-mp="3592kzp898bl5">
                      Learner
                    </th>
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 hidden sm:table-cell" data-id="element-2136" data-mp="1awcqb5a2aycp">
                      Email
                    </th>
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-2137" data-mp="3jebbnga985nc">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2138" data-mp="3aclhi2ozmslv">
                  {importedLearners.map((learner, index) => <motion.tr key={learner.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4 + index * 0.05
                }} className="hover:bg-gray-50 transition-colors" data-id="element-2139" data-mp="awyn1y4f7pfl" data-mpi={index}>
                      <td className="py-3 px-5" data-id="element-2140" data-mp="2mp3vknsemoqt">
                        <div className="flex items-center gap-3" data-id="element-2141" data-mp="2mmhf1ynjcywg">
                          <div className="w-8 h-8 rounded-full bg-[#30718D]/10 flex items-center justify-center text-[#30718D] text-xs font-bold flex-shrink-0" data-id="element-2142" data-mp="2hozcqr40xhxf">
                            {learner.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div data-id="element-2143" data-mp="h5va7lfff83">
                            <div className="font-medium text-gray-900 text-sm" data-id="element-2144" data-mp="hxtlq6err22">
                              {learner.name}
                            </div>
                            <div className="text-xs text-gray-500 sm:hidden" data-id="element-2145" data-mp="t4ik6of27z4k">
                              {learner.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-5 text-sm text-gray-600 hidden sm:table-cell" data-id="element-2146" data-mp="j6clefojv93x">
                        {learner.email}
                      </td>
                      <td className="py-3 px-5" data-id="element-2147" data-mp="33wjy1trkdh6n">
                        {learner.status === 'success' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#007558]/10 text-[#007558] text-xs font-medium" data-id="element-2148" data-mp="38vwnmm9tl2cx">
                            <CheckCircle size={14} data-id="element-2149" data-mp="31b3yr4nhewn5" />
                            <span data-id="element-2150" data-mp="2we3oehjln7q8">Ready</span>
                          </div>}
                        {learner.status === 'warning' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium" title={learner.message} data-id="element-2151" data-mp="3adli9bbr5b6g">
                            <AlertTriangle size={14} data-id="element-2152" data-mp="2hoelohvuyvnu" />
                            <span data-id="element-2153" data-mp="2hp7hinta40r7">Update</span>
                          </div>}
                        {learner.status === 'error' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium" title={learner.message} data-id="element-2154" data-mp="2doxd3fl4xieg">
                            <XCircle size={14} data-id="element-2155" data-mp="2mmptkn2lew3f" />
                            <span data-id="element-2156" data-mp="2mlxxlb532wcm">Error</span>
                          </div>}
                      </td>
                    </motion.tr>)}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 text-center" data-id="element-2157" data-mp="1tx95u2px43dj">
              <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2158" data-mp="3u1326sp0kij9">
                View all 124 records
              </button>
            </div>
          </motion.div>

          {/* Right Column: Assignment Controls */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4
        }} className="space-y-6" data-id="element-2159" data-mp="cl4qd22rjz4e">
            {/* Pathways Assignment */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2160" data-mp="3g3er2ckr0pre">
              <div className="p-5 border-b border-gray-200 bg-gray-50/50" data-id="element-2161" data-mp="3l313wkwo03zo">
                <div className="flex items-center gap-2 mb-1" data-id="element-2162" data-mp="3p3tb78lz7ytb">
                  <Map size={18} className="text-[#30718D]" data-id="element-2163" data-mp="10b7cz2nwscvm" />
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-2164" data-mp="10cs56lmr3jqb">
                    Assign Pathways
                  </h2>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2165" data-mp="2ot0h1r186e05">
                  Select learning pathways for all imported users.
                </p>
              </div>

              <div className="p-5" data-id="element-2166" data-mp="2g1rrn5x5e9dt">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-2167" data-mp="2g0q5o6elg2hb">
                  <label htmlFor="toggle-all-pathways" className="text-sm font-semibold text-gray-900 cursor-pointer" data-id="element-2168" data-mp="2b1ltd6wdhomr">
                    Enable All Pathways
                  </label>
                  <button id="toggle-all-pathways" role="switch" aria-checked={enableAllPathways} onClick={() => setEnableAllPathways(!enableAllPathways)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${enableAllPathways ? 'bg-[#30718D]' : 'bg-gray-200'}`} data-id="element-2169" data-mp="2147q68zgmwce">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableAllPathways ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2170" data-mp="3mhtzfib0ji36" />
                  </button>
                </div>

                <div className="space-y-3" data-id="element-2171" data-mp="30bu9rgi8mnzq">
                  {availablePathways.map(pathway => <label key={pathway} className="flex items-start gap-3 cursor-pointer group" data-id="element-2172" data-mp="2lmy4fr7kfp2t">
                      <div className="flex items-center h-5 mt-0.5" data-id="element-2173" data-mp="2qlhrxw1t58xa">
                        <input type="checkbox" checked={selectedPathways.includes(pathway)} onChange={() => togglePathway(pathway)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] transition-colors" data-id="element-2174" data-mp="2qjoiya3q2cm4" />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-2175" data-mp="22ou85xcs1kif">
                        {pathway}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>

            {/* Skills Assignment */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2176" data-mp="5vsg7rwei38t">
              <div className="p-5 border-b border-gray-200 bg-gray-50/50" data-id="element-2177" data-mp="1qcrvlkogalje">
                <div className="flex items-center gap-2 mb-1" data-id="element-2178" data-mp="1leq9srzn4848">
                  <BookOpen size={18} className="text-[#30718D]" data-id="element-2179" data-mp="1lgb2sd6b3izz" />
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-2180" data-mp="204folq0noamz">
                    Assign Skills
                  </h2>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2181" data-mp="1vatcbyyem8rp">
                  Select individual skills for all imported users.
                </p>
              </div>

              <div className="p-5" data-id="element-2182" data-mp="2446e7qeg4kpx">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-2183" data-mp="247c1r17ffejj">
                  <label htmlFor="toggle-all-skills" className="text-sm font-semibold text-gray-900 cursor-pointer" data-id="element-2184" data-mp="2izw2jv73t9xt">
                    Enable All Skills
                  </label>
                  <button id="toggle-all-skills" role="switch" aria-checked={enableAllSkills} onClick={() => setEnableAllSkills(!enableAllSkills)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${enableAllSkills ? 'bg-[#30718D]' : 'bg-gray-200'}`} data-id="element-2185" data-mp="ooh9b4evwsdb">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableAllSkills ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2186" data-mp="2mj7tl18g1cib" />
                  </button>
                </div>

                <div className="space-y-3 max-h-[240px] overflow-y-auto pr-2" data-id="element-2187" data-mp="1tw3wdifle2h3">
                  {availableSkills.map(skill => <label key={skill} className="flex items-start gap-3 cursor-pointer group" data-id="element-2188" data-mp="1oybubvl8gzz6">
                      <div className="flex items-center h-5 mt-0.5" data-id="element-2189" data-mp="23v2wgcmakpsh">
                        <input type="checkbox" checked={selectedSkills.includes(skill)} onChange={() => toggleSkill(skill)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] transition-colors" data-id="element-2190" data-mp="1ytn9gf0k097g" />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-2191" data-mp="3ac6ggwswsr15">
                        {skill}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}